import React from 'react';
import { TCN } from '../ui-kit/tokens';
import TabBar from './TabBar';

/**
 * TCN AppShell — mobile chrome.
 * iOS-flavored : status-bar spacer (44pt), header slot, scrollable content,
 * sticky bottom TabBar (with safe-area inset-bottom).
 *
 * Sign-out is exposed via the leading <AccountAvatar /> in each page's TopBar
 * (tap → bottom sheet) — no chrome-level button to avoid colliding with the
 * trailing slot (e.g. Morning Brief icon on the Today tab).
 *
 * Props:
 *  - tab: 'today' | 'creators' | 'tiers' | 'payout' | 'actions' — active TabBar tab
 *  - onTabChange: (id) => void
 *  - showTabs: boolean (default true)
 *  - header: ReactNode rendered between status bar and content
 *  - contentStyle: extra style for the scroll container
 *  - statusBar: boolean (default true) — shows the iOS status bar spacer
 */
const AppShell = ({
  tab,
  onTabChange,
  showTabs = true,
  header,
  children,
  contentStyle,
  statusBar = true,
  className = '',
  style,
}) => {
  return (
    <div
      className={`flex flex-col w-full h-[100dvh] ${className}`}
      style={{
        background: TCN.bg,
        color: TCN.text,
        fontFamily: TCN.fontSans,
        position: 'relative',
        ...style,
      }}
    >
      {statusBar && (
        <div
          aria-hidden="true"
          style={{
            // Audit fix #59 — height adds the safe-area-inset, doesn't pad it
            // (otherwise on iPhone X+ with 47-59px notch the chrome stays 44px
            // and content bleeds into the notch).
            height: 'calc(44px + env(safe-area-inset-top, 0px))',
            background: TCN.bg,
            flexShrink: 0,
          }}
        />
      )}
      {header && <div style={{ flexShrink: 0 }}>{header}</div>}

      <div
        className="flex-1 overflow-y-auto"
        style={{
          background: TCN.bg,
          // Audit fix #59 — bottom padding includes the safe-area-inset so the
          // tab bar (which itself adds env(safe-area-inset-bottom)) never hides
          // content on iPhone X+ models with a 34px home indicator gutter.
          paddingBottom: showTabs ? 'calc(96px + env(safe-area-inset-bottom, 0px))' : 0,
          // iOS native momentum scroll (audit fix #43, 2026-04-28)
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          // Belt-and-suspenders to the html.cap-locked lock: even if a child
          // ever overflows horizontally (1px is enough), this scroller refuses
          // to let it drag the page sideways. No legit TCN content scrolls X.
          overflowX: 'hidden',
          ...contentStyle,
        }}
      >
        {children}
      </div>

      {showTabs && <TabBar active={tab} onChange={onTabChange} />}
    </div>
  );
};

export default AppShell;
